import React from "react";
import { Admin, Resource } from "react-admin";
import { DoctorList, DoctorEdit2, DoctorCreate } from "./components/Doctors";
import {
  ClinicList,
  ClinicCreate,
  TabledClinicEdit,
} from "./components/Clinics";
import {
  PatientList,
  PatientCreate,
  TabledPatientEdit,
} from "./components/Patients";
import authProvider from "./misc/authProvider";
import RedirectComponent from "./misc/RedirectComponent";
import customRoutes from "./misc/customRoutes";
import MyLayout from "./layout/myLayout";
import {
  RunningSessionList,
  RunningSessionShow,
} from "./components/Sessions/RunningSessions";
import {
  PowerdotCompletedSessionList,
  PowerdotCompletedSessionShow,
} from "./components/Sessions/PowerdotCompletedSessions";
import {
  ScheduledSessionsList,
  ScheduledSessionsShow,
} from "./components/Sessions/ScheduledSessions";
import MainAuditList from "./components/Audit/MainAuditDrawer";
import { UserAccessList } from "./components/Audit/UserAccess";
import {
  UnverifiedUserList,
  UnverifiedUserEdit,
} from "./components/UnverifiedUsers";
import { MissedSessionList } from "./components/Sessions/MissedSessions";
import MuscleImageList from "./components/Images/MuscleGroupImage";
import SetupImageList from "./components/Images/SetupImage";
import {
  TransferSharingRequestList,
  SharingList,
  TransferList,
} from "./components/Transfers";
import { StaffList, StaffShow, StaffCreate } from "./components/Staff";
import { StationList, StationShow, StationEdit2 } from "./components/Stations";
import {
  ActivationList,
  ActivationCreate,
  ActivationDeactivate,
} from "./components/Stations/Activation";
import { DeviceEdit } from "./components/Stations/Devices";
import reducers from "./redux/reducers";
import sagas from "./redux/sagas";
import { newDataProvider } from "./misc/dataProvider";

import UserIcon from "@material-ui/icons/Group";
import LocalHospital from "@material-ui/icons/LocalHospital";
import PeopleOutline from "@material-ui/icons/PeopleOutline";
import Lock from "@material-ui/icons/Lock";
import Transfer from "@material-ui/icons/TransferWithinAStation";
import Schedule from "@material-ui/icons/Schedule";
import Sync from "@material-ui/icons/Sync";
import ShareIcon from "@material-ui/icons/Share";
import PageView from "@material-ui/icons/Pageview";
import TimerOff from "@material-ui/icons/TimerOff";
import QueuePlayNextIcon from "@material-ui/icons/QueuePlayNext";
import FlipToFrontIcon from "@material-ui/icons/FlipToFront";
import DeveloperBoardIcon from "@material-ui/icons/DeveloperBoard";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import BusinessIcon from "@material-ui/icons/Business";
import {
  TheragunCompletedSessionList,
  TheragunCompletedSessionShow
} from "./components/Sessions/TheragunCompletedSessions";
import {TheragunIcon, PowerdotIcon} from "./misc/customComponents";

export const dataProvider = newDataProvider();

const App = () => (
  <Admin
    dataProvider={dataProvider}
    authProvider={authProvider}
    loginPage={RedirectComponent}
    customRoutes={customRoutes}
    appLayout={MyLayout}
    customReducers={reducers}
    customSagas={sagas}
  >
    <Resource
      name="doctors"
      list={DoctorList}
      edit={DoctorEdit2}
      create={DoctorCreate}
      icon={LocalHospital}
      options={{ label: "Clinicians" }}
    />
    <Resource
      name="clinics"
      list={ClinicList}
      edit={TabledClinicEdit}
      options={{ label: "Clinics" }}
      create={ClinicCreate}
      icon={BusinessIcon}
    />
    <Resource
      name="patients"
      list={PatientList}
      edit={TabledPatientEdit}
      create={PatientCreate}
      icon={UserIcon}
      options={{ label: "Patients" }}
    />
    <Resource
      name="staff"
      list={StaffList}
      edit={StaffShow}
      create={StaffCreate}
      options={{ label: "Staff" }}
      icon={AssignmentIndIcon}
    />
    <Resource
      list={StationList}
      name="stations"
      show={StationShow}
      edit={StationEdit2}
      options={{ label: "Stations", subMenu: true, root: "stations" }}
      icon={DeveloperBoardIcon}
    />
    <Resource
      list={ActivationList}
      create={ActivationCreate}
      edit={ActivationDeactivate}
      name="station_activations"
      options={{ label: "Activations", subMenu: true, root: "stations" }}
      icon={PersonAddIcon}
    />
    <Resource
      name="scheduled_sessions"
      list={ScheduledSessionsList}
      show={ScheduledSessionsShow}
      edit={ScheduledSessionsShow}
      icon={Schedule}
      options={{ label: "Scheduled Sessions", subMenu: true, root: "sessions" }}
    />
    <Resource
      name="running_sessions"
      list={RunningSessionList}
      show={RunningSessionShow}
      edit={RunningSessionShow}
      options={{ label: "Running Sessions", subMenu: true, root: "sessions" }}
      icon={Sync}
    />
    <Resource
      name="powerdot_completed_sessions"
      list={PowerdotCompletedSessionList}
      show={PowerdotCompletedSessionShow}
      edit={PowerdotCompletedSessionShow}
      options={{ label: "Completed Sessions", subMenu: true, root: "sessions" }}
      icon={PowerdotIcon}
    />
    <Resource
        name="theragun_completed_sessions"
        list={TheragunCompletedSessionList}
        show={TheragunCompletedSessionShow}
        edit={TheragunCompletedSessionShow}
        options={{ label: "Completed Sessions", subMenu: true, root: "sessions" }}
        icon={TheragunIcon}
    />
    <Resource
      name="missed_sessions"
      list={MissedSessionList}
      options={{ label: "Missed Sessions", subMenu: true, root: "sessions" }}
      icon={TimerOff}
    />
    <Resource
      name="main_audit"
      list={MainAuditList}
      options={{ label: "Audit", subMenu: true, root: "audit" }}
      icon={Lock}
    />
    <Resource
      name="user_access"
      list={UserAccessList}
      options={{ label: "User Access", subMenu: true, root: "audit" }}
      icon={Transfer}
    />
    <Resource
      name="unverified_users"
      list={UnverifiedUserList}
      edit={UnverifiedUserEdit}
      options={{ label: "Unverified Users", subMenu: true, root: "audit" }}
      icon={PeopleOutline}
    />
    <Resource
      name="muscle_group_images"
      list={MuscleImageList}
      options={{ label: "Muscle Images", subMenu: true, root: "images" }}
      icon={PageView}
    />
    <Resource
      name="setup_images"
      list={SetupImageList}
      options={{ label: "Setup Images", subMenu: true, root: "images" }}
      icon={PageView}
    />
    <Resource
      name="transfer_sharing_requests"
      list={TransferSharingRequestList}
      options={{ label: "Requests", subMenu: true, root: "transfer" }}
      icon={QueuePlayNextIcon}
    />
    <Resource
      name="sharings"
      list={SharingList}
      options={{ label: "Sharings", subMenu: true, root: "transfer" }}
      icon={ShareIcon}
    />
    <Resource
      name="transfers"
      list={TransferList}
      options={{ label: "Transfers", subMenu: true, root: "transfer" }}
      icon={FlipToFrontIcon}
    />
    <Resource name="all_sessions" />
    <Resource name="finished_sessions" />
    <Resource name="unfinished_sessions" />
    <Resource name="remote_sessions" />
    <Resource name="clinic_members" />
    <Resource name="clinic_invites" />
    <Resource name="station_devices" edit={DeviceEdit} />
  </Admin>
);

export default App;
